import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Box from '@mui/material/Box';

import { setDataAction } from 'store/actions/claimFormActions';
import {
  Typography,
  colors,
  Dropdown,
  IconGraphy,
  Input,
  Button,
  Radio,
  Alert,
} from '@leapeasy/ui-kit';
import ClaimDatePickerInput from 'components/ClaimForm/ClaimDatePickerInput';
import styled from '@emotion/styled';

const EMPTY_CLAIM_TYPE = {
  damageOnly: false,
  noShow: false,
  tenantDefault: false,
  damages: false,
  holdOver: false,
};

/** Damages Only / No Show are exclusive — never retain other claim types alongside them. */
const sanitizeClaimType = (raw = {}) => {
  if (raw.damageOnly) {
    return { ...EMPTY_CLAIM_TYPE, damageOnly: true };
  }
  if (raw.noShow) {
    return { ...EMPTY_CLAIM_TYPE, noShow: true };
  }
  return {
    ...EMPTY_CLAIM_TYPE,
    tenantDefault: !!raw.tenantDefault,
    damages: !!raw.damages,
    holdOver: !!raw.holdOver,
  };
};

const getClaimTypes = (claimType) => {
  const damagesOnlySelected = !!claimType.damageOnly;
  const noShowSelected = !!claimType.noShow;

  return [
    {
      text: 'Damages Only',
      value: 'damageOnly',
      tooltip:
        'This claim is only being submitted for damages beyond normal wear and tear and there is no resident default.',
      icon: <IconGraphy icon="HomePlaces.Destruction" />,
      disabled: noShowSelected,
      type: 'item',
      placement: 'bottom',
    },
    {
      text: 'No Show',
      value: 'noShow',
      tooltip:
        'Any resident who fails to take occupancy of the assigned bed on the effective date of the lease (student housing only).',
      disabled: damagesOnlySelected,
      icon: <IconGraphy icon="Users.PersonSearch" />,
      type: 'item',
      placement: 'bottom',
    },
    {
      text: 'Resident Default',
      value: 'tenantDefault',
      tooltip: 'A resident has defaulted on their rent, or has skipped or abandoned their unit.',
      disabled: damagesOnlySelected || noShowSelected,
      type: 'item',
      placement: 'top',
      icon: <IconGraphy icon="Users.PersonAlert" />,
    },
    {
      text: 'Holdover',
      value: 'holdOver',
      tooltip:
        "Any additional monthly rent reflected in the company's books that is charged to a resident who fails to vacate a unit at the end of the lease term.",
      disabled: damagesOnlySelected || noShowSelected,
      icon: <IconGraphy icon="HomePlaces.HomeAndGarden" />,
      type: 'item',
      placement: 'top',
    },
    {
      text: 'Damages + Other',
      value: 'damages',
      tooltip:
        'Physical damage to the unit caused by the resident in violation of the terms of the lease, exclusive of damages resulting from normal wear and tear.',
      disabled: damagesOnlySelected || noShowSelected,
      icon: <IconGraphy icon="HomePlaces.Destruction" />,
      type: 'item',
      placement: 'top',
    },
  ];
};

const DAMAGE_TYPES = [
  {
    text: 'Trash',
    value: 'trash',
    type: 'item',
  },
  {
    text: 'Scratches/Holes/Burns',
    value: 'scratches',
    type: 'item',
  },
  {
    text: 'Other',
    value: 'other',
    type: 'item',
  },
];

const PLACE_DAMAGES = [
  {
    text: 'Appliance Damages',
    value: 'application',
    type: 'item',
    style: { color: '#b32279', backgroundColor: '#fcf0f8' },
  },
  {
    text: 'Carpet/Flooring',
    value: 'carpert',
    type: 'item',
    style: { color: '#b32279', backgroundColor: '#fcf0f8' },
  },
  {
    text: 'Wall/Ceilings',
    value: 'wall',
    type: 'item',
    style: { color: '#b32279', backgroundColor: '#fcf0f8' },
  },
  {
    text: 'Other',
    value: 'other',
    type: 'item',
    style: { color: '#b32279', backgroundColor: '#fcf0f8' },
  },
];

const Form2 = (props) => {
  const { actions, token, formData, handleNext, handleBack } = props;

  const [acceptableDocument, setAcceptableDocument] = useState('');
  const [depositAmount, setDepositAmount] = useState(0);
  const [agreeRadio, setRadio] = useState(null);
  const [agreeDate, setAgreeDate] = useState(null);
  const [relatedExpense, setRelatedExpense] = useState(null);
  const [damageType, setDamageType] = useState({
    trash: false,
    scratches: false,
    other: false,
  });
  const [placeDamages, setPlaceDamages] = useState({
    application: false,
    carpert: false,
    wall: false,
    other: false,
  });
  const [claimType, setClaimType] = useState({ ...EMPTY_CLAIM_TYPE });

  useEffect(() => {
    if (!formData?.data) {
      return;
    }
    setAgreeDate(formData.data.agreePayDate || null);
    setDepositAmount(formData.data.depositAmount);
    setRadio(formData.data.paymentAgreement || null);
    setRelatedExpense(formData.data.relatedExpense);
    setClaimType(sanitizeClaimType(formData.data.claimType));
    setPlaceDamages(formData.data.placeDamages || {});
    setDamageType(formData.data.damageType || {});
    setAcceptableDocument(formData.data.acceptableDocument || '');
  }, [formData]);

  const handleDepositAmount = (event) => {
    setDepositAmount(event.target.value);
  };

  const handleDate = (e) => {
    setAgreeDate(e.target.value);
  };

  const handleClaimType = (e) => {
    const types = Array.isArray(e.target.value) ? e.target.value : [];
    if (!types.includes('noShow')) {
      setAcceptableDocument('');
    }

    // Damages Only is exclusive: disable/clear every other claim type (no silent retain).
    if (types.includes('damageOnly')) {
      setClaimType({ ...EMPTY_CLAIM_TYPE, damageOnly: true });
      setDamageType({});
      setPlaceDamages({});
      return;
    }

    if (types.includes('noShow')) {
      setClaimType({ ...EMPTY_CLAIM_TYPE, noShow: true });
      setRelatedExpense('No');
      setDamageType({});
      setPlaceDamages({});
      return;
    }

    // Damages + Other path — re-enable multi-select; never keep damageOnly/noShow.
    setClaimType(
      sanitizeClaimType({
        ...types.reduce((prev, cur) => ({ ...prev, [cur]: true }), {}),
      }),
    );
    setDamageType({});
    setPlaceDamages({});
  };

  const handleDamageType = (e) => {
    const types = e.target.value;

    setDamageType({
      ...types.reduce((prev, cur) => ({ ...prev, [cur]: true }), {}),
    });
  };

  const handlePlaceDamages = (e) => {
    const places = e.target.value;

    setPlaceDamages({
      ...places.reduce((prev, cur) => ({ ...prev, [cur]: true }), {}),
    });
  };

  const handleNextButton = () => {
    actions.setDataAction({
      ...formData.data,
      paymentAgreement: agreeRadio,
      agreePayDate: agreeDate,
      depositAmount: depositAmount,
      claimType: sanitizeClaimType(claimType),
      relatedExpense: relatedExpense,
      damageType: damageType,
      placeDamages: placeDamages,
      acceptableDocument: acceptableDocument,
    });
    handleNext();
  };
  let disable = true;
  if (
    depositAmount !== null &&
    (claimType.damageOnly ||
      claimType.damages ||
      claimType.holdOver ||
      claimType.noShow ||
      claimType.tenantDefault)
  ) {
    if (claimType.damageOnly || claimType.damages) {
      if (
        (damageType.other || damageType.scratches || damageType.trash) &&
        (placeDamages.application ||
          placeDamages.carpert ||
          placeDamages.other ||
          placeDamages.wall) &&
        agreeRadio
      ) {
        if (agreeRadio === 'Yes') disable = agreeDate ? false : true;
        else disable = false;
      } else disable = true;
    } else if (claimType.holdOver || claimType.noShow || claimType.tenantDefault) {
      if (relatedExpense && agreeRadio) {
        if (agreeRadio === 'Yes') disable = agreeDate ? false : true;
        else disable = false;
      } else disable = true;

      if (claimType.noShow) {
        if (acceptableDocument === '') disable = true;
        else disable = false;
      }
    }
  }

  const claimTypeValue = useMemo(() => {
    const sanitized = sanitizeClaimType(claimType);
    return Object.keys(sanitized).filter((type) => sanitized[type]);
  }, [claimType]);

  const damageTypeValue = useMemo(() => {
    return Object.keys(damageType).filter((type) => damageType[type]);
  }, [damageType]);

  const placeDamagesValue = useMemo(() => {
    return Object.keys(placeDamages).filter((type) => placeDamages[type]);
  }, [placeDamages]);

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" gap="4px">
        <Dropdown
          value={claimTypeValue}
          size="medium"
          onChange={handleClaimType}
          options={getClaimTypes(claimType)}
          label="Type of Claim"
          icon={<IconGraphy icon="HomePlaces.Destruction" />}
          iconVisible
          showSearch
          multiselect
          style={{
            minWidth: '100%',
          }}
        />
      </Box>

      {(claimType.noShow || claimType.holdOver || claimType.tenantDefault) && (
        <Box display="flex" flexDirection="column" gap="12px" mb={2}>
          <Typography variant="small" style={{ color: colors.purple[900], fontWeight: '400' }}>
            Are there other related expenses for this claim?
          </Typography>
          <Typography variant="small" style={{ color: colors.neutral[900], fontWeight: '400' }}>
            Related expenses are legal and filing expenses for eviction proceedings paid to third
            parties as a result of resident default.
          </Typography>
          <Box display="flex" alignItems="flex-start" sx={{ gap: '24px' }}>
            <Radio
              disabled={claimType.noShow}
              label="Yes"
              value={relatedExpense === 'Yes'}
              onChange={(_) => setRelatedExpense('Yes')}
            />
            <Radio
              disabled={claimType.noShow}
              label="No"
              value={relatedExpense === 'No'}
              onChange={(_) => setRelatedExpense('No')}
            />
          </Box>

          {claimType.noShow && (
            <React.Fragment>
              <Typography variant="small" style={{ color: colors.neutral[900], fontWeight: '400' }}>
                Select which acceptable document you'll upload that verifies the resident's move-in
                intent within 90 days prior to their lease start date.
              </Typography>
              <StyledDropdown>
                <Dropdown
                  label="Acceptable Document"
                  placeholder="Select an acceptable document"
                  showSearch={false}
                  multiselect={false}
                  value={acceptableDocument}
                  onChange={(e) => setAcceptableDocument(e.target.value)}
                  options={[
                    {
                      text: 'The lease agreement signed and dated within 90 days of the lease start date',
                      value:
                        'The Lease agreement signed and dated within 90 days of the lease start date',
                    },
                    {
                      text: 'Roommate agreement that is signed and dated within 90 days of the lease start date',
                      value:
                        'Roommate Agreement that is signed and dated within 90-days of the lease start date',
                    },
                    {
                      text: 'Move-in inspection report that is signed and dated within 90 days of the lease start date',
                      value:
                        'Move-in inspection report that is signed and dated within 90 days of the lease start date',
                    },
                    {
                      text: 'Documented communication from the resident showing move-in intent (must be dated within 90 days of lease start). Note: Intent = yes, I am moving in or no, I am not moving in.',
                      value:
                        'Documented communication from the resident showing move-in intent (must be dated within 90 days of lease start). Note: Intent = yes, I am moving in or no, I am not moving in.',
                    },
                  ]}
                />
              </StyledDropdown>
            </React.Fragment>
          )}
          {acceptableDocument && (
            <React.Fragment>
              <Alert
                showIcon
                showClose={false}
                showBorder={false}
                type="info"
                variant="contained"
                description="Please be sure to upload related expense documents and invoices under Other Documents on step 7."
              />
            </React.Fragment>
          )}

          {/* {claimType.noShow && (
            <Alert
              showIcon
              showClose={false}
              showBorder={false}
              type="info"
              variant="contained"
              description={`Please upload any document or documentation that confirms the resident's intent to move in the unit within 90 days of the lease start date under Other Documents on page(5)`}
            />
          )} */}
          {/* 
          {!claimType.noShow && relatedExpense === 'Yes' && (
            <Alert
              showIcon
              showClose={false}
              showBorder={false}
              type="info"
              variant="contained"
              description={`Please be sure to upload related expense documents and invoices under Other Documents on page(5)`}
            />
          )} */}
        </Box>
      )}

      {(claimType.damageOnly || claimType.damages) && (
        <Box>
          <Dropdown
            value={damageTypeValue}
            size="medium"
            onChange={handleDamageType}
            options={DAMAGE_TYPES}
            label="Type of Damages"
            iconVisible
            showSearch
            multiselect
            style={{
              minWidth: '100%',
            }}
          />

          <Dropdown
            value={placeDamagesValue}
            size="medium"
            onChange={handlePlaceDamages}
            options={PLACE_DAMAGES}
            label="Place of Damages"
            iconVisible
            showSearch
            multiselect
            style={{
              minWidth: '100%',
            }}
          />
        </Box>
      )}

      <Box display="flex" flexDirection="column" gap="12px" mb={2}>
        <Typography variant="body1" style={{ color: colors.purple[900], fontWeight: '400' }}>
          Have you worked out a payment agreement timeline?
        </Typography>

        <Box display="flex" sx={{ gap: '24px' }}>
          <Radio
            label="Yes"
            value={agreeRadio === 'Yes'}
            onChange={(_) => {
              setRadio('Yes');
            }}
          />

          <Radio
            label="No"
            value={agreeRadio === 'No'}
            onChange={(e) => {
              setAgreeDate(null);
              setRadio('No');
            }}
          />
        </Box>
      </Box>

      {agreeRadio === 'Yes' && (
        <ClaimDatePickerInput
          label="Agreed Payment Date"
          value={agreeDate || ''}
          minWidth="100%"
          minDate="01/01/2018"
          size="medium"
          showActionButtons={false}
          onChange={(e) => handleDate(e)}
          placeholder="Select a date"
          fullWidth
        />
      )}

      <Input
        value={depositAmount || '0'}
        onChange={handleDepositAmount}
        label="Current Security Deposit Amount"
        type="number"
        icon={<IconGraphy icon="Finance.AttachMoney" />}
        minWidth="100%"
        fullWidth
        size="medium"
        showCopy={false}
      />

      <Box
        sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', mt: 4, gap: '16px' }}
      >
        <Button size="medium" variant="secondary" onClick={handleBack} style={{ flexShrink: 2 }}>
          Back
        </Button>

        <Button
          size="medium"
          variant="primary"
          onClick={handleNextButton}
          disabled={disable}
          style={{ flexShrink: 1 }}
        >
          Next
        </Button>
      </Box>
    </Fragment>
  );
};

const StyledDropdown = styled.div`
  & > div {
    & > div:nth-of-type(2) {
      height: auto;
      min-height: -60px;
      & > div:nth-of-type(2) {
        & > div {
          & > div {
            height: auto;
          }
        }
      }
    }
  }
`;

const mapStateToProps = (state) => {
  return {
    formData: state.getIn(['formData']),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(
      {
        setDataAction,
      },
      dispatch,
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Form2);
